import { reactive, computed, watchEffect } from "vue";
import { fabric } from "fabric";

import { store } from "@/store";

import { canvas } from "@/canvas-api/Canvas";

import { ActionTypes } from "@/store/actions";
import { CanvasActions, Transform, AccordianState } from "@/types";

const accordianState = computed<AccordianState>(() => store.getters.accordianState);

export const undoActionStack = reactive<CanvasActions[]>([]);
export const redoActionStack = reactive<CanvasActions[]>([]);

const handleStackSize = (stack: CanvasActions[]) => {
  if (stack.length > 20) {
    stack.shift();
  }
};

watchEffect(() => handleStackSize(undoActionStack));
watchEffect(() => handleStackSize(redoActionStack));

export const undo = () => {
  const action = undoActionStack.pop();

  if (action) {
    handleUndo(action);
  }
};

export const redo = () => {
  const action = redoActionStack.pop();

  if (action) {
    handleRedo(action);
  }
};

export const handleUndo = (action: CanvasActions) => {
  const objects = canvas.value.getObjects() as fabric.Object[];

  const target = objects.find((obj: fabric.Object) => {
    if (obj.id && obj.id === action.id) {
      return obj;
    }
  });

  switch (action.type) {
    case "canvasBgColor": {
      if (action.canvasBgColor) {
        const currentLabelBgColor = computed(() => store.getters.currentLabelBgColor);

        redoActionStack.push({
          id: 0,
          type: "canvasBgColor",
          canvasBgColor: currentLabelBgColor.value,
        });

        store.dispatch(ActionTypes.SET_LABEL_BG_COLOR, action.canvasBgColor);
      }
      break;
    }
    case "add": {
      if (target) {
        const clone = fabric.util.object.clone(target);

        redoActionStack.push({ id: target.id as number, type: "add", object: clone });

        canvas.value.remove(target);

        store.dispatch(ActionTypes.SET_ACCORDIAN_STATE, {
          ...accordianState.value,
          isOpen: false,
        });
      }
      break;
    }
    case "delete": {
      if (action.object) {
        canvas.value.add(action.object);
        canvas.value.setActiveObject(action.object).renderAll();

        redoActionStack.push({ id: action.object.id as number, type: "delete" });
      }
      break;
    }

    case "positionSet": {
      if (target && action.object) {
        const clone = fabric.util.object.clone(target);

        redoActionStack.push({ id: target.id as number, type: "positionSet", object: clone });

        target
          .set({
            left: action.object.left,
            top: action.object.top,
          })
          .setCoords();

        canvas.value.setActiveObject(target);

        canvas.value.fire("object:actionStack", {
          target,
          transform: {
            action: "positionSet",
            original: clone,
          },
        });
      }
      break;
    }
    case "drag":
    case "rotate":
    case "scale": {
      if (target && action.transform) {
        const clone = fabric.util.object.clone(target);

        switch (action.type) {
          case "drag": {
            redoActionStack.push({
              id: target.id as number,
              type: "drag",
              transform: clone,
            });

            target
              .set({
                left: action.transform.left,
                top: action.transform.top,
              })
              .setCoords();

            canvas.value.fire("object:actionStack", {
              target,
              transform: {
                action: "drag",
                original: clone,
              },
            });
            break;
          }
          case "rotate": {
            redoActionStack.push({
              id: target.id as number,
              type: "rotate",
              transform: clone,
            });

            target
              .set({
                angle: action.transform.angle,
              })
              .setCoords();

            canvas.value.fire("object:actionStack", {
              target,
              transform: {
                action: "rotate",
                original: clone,
              },
            });
            break;
          }
          case "scale": {
            redoActionStack.push({
              id: target.id as number,
              type: "scale",
              transform: clone,
            });

            target
              .set({
                scaleX: action.transform.scaleX,
                scaleY: action.transform.scaleY,
              })
              .setCoords();

            canvas.value.fire("object:actionStack", {
              target,
              transform: {
                action: "scale",
                original: clone,
              },
            });
            break;
          }
        }

        canvas.value.setActiveObject(target);
        canvas.value.renderAll();
      }
      break;
    }
    case "fontSize":
    case "fontWeight":
    case "fontStyle":
    case "underline":
    case "textAlign":
    case "fontFamily": {
      if (target && action.object) {
        const stackObject = action.object as fabric.IText;
        const iTextTarget = target as fabric.IText;

        const clone = fabric.util.object.clone(target);

        switch (action.type) {
          case "fontFamily": {
            redoActionStack.push({
              id: target.id as number,
              type: "fontFamily",
              object: clone,
            });

            iTextTarget.set({
              fontFamily: stackObject.fontFamily as string,
              dirty: true,
            });
            break;
          }
          case "fontSize": {
            redoActionStack.push({
              id: target.id as number,
              type: "fontSize",
              object: clone,
            });

            iTextTarget.set({
              fontSize: stackObject.fontSize as number,
              dirty: true,
            });
            break;
          }
          case "fontWeight": {
            redoActionStack.push({
              id: target.id as number,
              type: "fontWeight",
              object: clone,
            });

            iTextTarget.set({
              fontWeight: stackObject.fontWeight as string,
              dirty: true,
            });
            break;
          }
          case "fontStyle": {
            redoActionStack.push({
              id: target.id as number,
              type: "fontStyle",
              object: clone,
            });

            iTextTarget.set({
              fontStyle: stackObject.fontStyle as "italic" | "normal",
              dirty: true,
            });
            break;
          }
          case "underline": {
            redoActionStack.push({
              id: target.id as number,
              type: "underline",
              object: clone,
            });

            iTextTarget.set({
              underline: stackObject.underline as boolean,
              dirty: true,
            });
            break;
          }
          case "textAlign": {
            redoActionStack.push({
              id: target.id as number,
              type: "textAlign",
              object: clone,
            });

            iTextTarget.set({
              textAlign: stackObject.textAlign as string,
              dirty: true,
            });
            break;
          }
        }

        canvas.value.setActiveObject(iTextTarget);
        canvas.value.renderAll();

        store.dispatch(ActionTypes.SET_ACCORDIAN_STATE, {
          ...accordianState.value,
          key: accordianState.value.key + 1,
        });
      }
      break;
    }
    case "stroke":
    case "strokeWidth":
    case "opacity":
    case "fill": {
      if (target && action.object) {
        const isGroup = (obj: fabric.Object): obj is fabric.Group => {
          const tmp = obj as fabric.Group;

          if (tmp._objects) {
            return true;
          } else {
            return false;
          }
        };

        const stackObject = action.object;
        switch (action.type) {
          case "opacity": {
            const clone = fabric.util.object.clone(target);

            target.set({
              opacity: stackObject.opacity as number,
              dirty: true,
            });

            redoActionStack.push({
              id: target.id as number,
              type: "opacity",
              object: clone,
            });
            break;
          }
          case "stroke": {
            if (isGroup(target) && isGroup(stackObject)) {
              target.clone(function(clone: fabric.Group) {
                redoActionStack.push({
                  id: target.id as number,
                  type: "stroke",
                  object: clone,
                });
              });

              target._objects.forEach((obj) => {
                obj.set({
                  stroke: stackObject._objects[0].stroke as string,
                  dirty: true,
                });
              });
            } else {
              const clone = fabric.util.object.clone(target);

              target.set({
                stroke: stackObject.stroke as string,
                dirty: true,
              });

              redoActionStack.push({
                id: target.id as number,
                type: "stroke",
                object: clone,
              });
            }
            break;
          }
          case "strokeWidth": {
            if (isGroup(target) && isGroup(stackObject)) {
              target.clone(function(clone: fabric.Group) {
                redoActionStack.push({
                  id: target.id as number,
                  type: "strokeWidth",
                  object: clone,
                });
              });

              target._objects.forEach((obj) => {
                obj.set({
                  strokeWidth: stackObject._objects[0].strokeWidth as number,
                  dirty: true,
                });
              });
            } else {
              const clone = fabric.util.object.clone(target);

              target.set({
                strokeWidth: stackObject.strokeWidth as number,
                dirty: true,
              });

              redoActionStack.push({
                id: target.id as number,
                type: "strokeWidth",
                object: clone,
              });
            }
            break;
          }
          case "fill": {
            if (isGroup(target) && isGroup(stackObject)) {
              target.clone(function(clone: fabric.Group) {
                redoActionStack.push({
                  id: target.id as number,
                  type: "fill",
                  object: clone,
                });
              });

              target._objects.forEach((obj) => {
                obj.set({
                  fill: stackObject._objects[0].fill as string,
                  dirty: true,
                });
              });
            } else {
              const clone = fabric.util.object.clone(target);

              target.set({
                fill: stackObject.fill as string,
                dirty: true,
              });

              redoActionStack.push({
                id: target.id as number,
                type: "fill",
                object: clone,
              });
            }
            break;
          }
        }

        canvas.value.setActiveObject(target);
        canvas.value.renderAll();

        store.dispatch(ActionTypes.SET_ACCORDIAN_STATE, {
          ...accordianState.value,
          key: accordianState.value.key + 1,
        });
        store.dispatch(ActionTypes.SET_CANVAS_ACTIVE_OBJECT, target);
      }
      break;
    }
    case "scaleX":
    case "scaleY": {
      if (target && action.object) {
        const stackObject = action.object;

        switch (action.type) {
          case "scaleX": {
            const clone = fabric.util.object.clone(target);

            target.set({
              scaleX: stackObject.scaleX as number,
              dirty: true,
            });

            redoActionStack.push({
              id: target.id as number,
              type: "scaleX",
              object: clone,
            });

            canvas.value.fire("object:actionStack", {
              target,
              transform: {
                action: "scaleX",
                original: clone,
              },
            });
            break;
          }
          case "scaleY": {
            const clone = fabric.util.object.clone(target);

            target.set({
              scaleY: stackObject.scaleY as number,
              dirty: true,
            });

            redoActionStack.push({
              id: target.id as number,
              type: "scaleY",
              object: clone,
            });

            canvas.value.fire("object:actionStack", {
              target,
              transform: {
                action: "scaleY",
                original: clone,
              },
            });
            break;
          }
        }

        canvas.value.setActiveObject(target);
        canvas.value.renderAll();

        store.dispatch(ActionTypes.SET_ACCORDIAN_STATE, {
          ...accordianState.value,
          key: accordianState.value.key + 1,
        });
        store.dispatch(ActionTypes.SET_CANVAS_ACTIVE_OBJECT, target);
      }
      break;
    }
    case "strokeDashArray": {
      if (target && action.object) {
        const stackObject = action.object;

        const clone = fabric.util.object.clone(target);

        target.set({
          strokeDashArray: stackObject.strokeDashArray as number[],
          dirty: true,
        });

        redoActionStack.push({
          id: target.id as number,
          type: "strokeDashArray",
          object: clone,
        });

        canvas.value.setActiveObject(target);
        canvas.value.renderAll();

        store.dispatch(ActionTypes.SET_ACCORDIAN_STATE, {
          ...accordianState.value,
          key: accordianState.value.key + 1,
        });
        store.dispatch(ActionTypes.SET_CANVAS_ACTIVE_OBJECT, target);
      }
      break;
    }
  }
};

export const handleRedo = (action: CanvasActions) => {
  const canvasObjects = canvas.value.getObjects() as fabric.Object[];

  const target = canvasObjects.find((obj: fabric.Object) => {
    if (obj.id && obj.id === action.id) {
      return obj;
    }
  });

  switch (action.type) {
    case "canvasBgColor": {
      if (action.canvasBgColor) {
        const currentLabelBgColor = computed(() => store.getters.currentLabelBgColor);

        undoActionStack.push({
          id: 0,
          type: "canvasBgColor",
          canvasBgColor: currentLabelBgColor.value,
        });

        store.dispatch(ActionTypes.SET_LABEL_BG_COLOR, action.canvasBgColor);
      }
      break;
    }
    case "add": {
      if (action.object) {
        action.object.clone(
          function(clone: fabric.Object) {
            undoActionStack.push({
              id: clone.id as number,
              type: "add",
              object: clone,
            });

            canvas.value.add(clone);
            canvas.value.setActiveObject(clone).renderAll();
          },
          ["name", "id"]
        );
      }
      break;
    }
    case "delete": {
      if (target) {
        const clone = fabric.util.object.clone(target);

        undoActionStack.push({ id: target.id as number, type: "delete", object: clone });

        canvas.value.remove(target);

        store.dispatch(ActionTypes.SET_ACCORDIAN_STATE, {
          ...accordianState.value,
          isOpen: false,
        });
      }
      break;
    }
    case "positionSet": {
      if (target && action.object) {
        const clone = fabric.util.object.clone(target);

        undoActionStack.push({ id: target.id as number, type: "positionSet", object: clone });

        target
          .set({
            left: action.object.left,
            top: action.object.top,
          })
          .setCoords();

        canvas.value.setActiveObject(target);

        canvas.value.fire("object:actionStack", {
          target,
          transform: {
            action: "positionSet",
            original: clone,
          },
        });
      }
      break;
    }
    case "drag":
    case "rotate":
    case "scale": {
      if (target && action.transform) {
        const transform = action.transform as Transform;

        const clone = fabric.util.object.clone(target);

        switch (action.type) {
          case "drag": {
            undoActionStack.push({
              id: target.id as number,
              type: "drag",
              transform: clone,
            });

            target
              .set({
                left: transform.left,
                top: transform.top,
              })
              .setCoords();

            canvas.value.fire("object:actionStack", {
              target,
              transform: {
                action: "drag",
                original: clone,
              },
            });
            break;
          }
          case "rotate": {
            undoActionStack.push({
              id: target.id as number,
              type: "rotate",
              transform: clone,
            });

            target
              .set({
                angle: transform.angle,
              })
              .setCoords();

            canvas.value.fire("object:actionStack", {
              target,
              transform: {
                action: "rptate",
                original: clone,
              },
            });
            break;
          }
          case "scale": {
            undoActionStack.push({
              id: target.id as number,
              type: "scale",
              transform: clone,
            });

            target
              .set({
                scaleX: transform.scaleX,
                scaleY: transform.scaleY,
              })
              .setCoords();

            canvas.value.fire("object:actionStack", {
              target,
              transform: {
                action: "scale",
                original: clone,
              },
            });
            break;
          }
        }

        canvas.value.setActiveObject(target);
        canvas.value.renderAll();
      }
      break;
    }
    case "fontSize":
    case "fontWeight":
    case "fontStyle":
    case "underline":
    case "textAlign":
    case "fontFamily": {
      if (target && action.object) {
        const stackObject = action.object as fabric.IText;
        const iTextTarget = target as fabric.IText;

        const clone = fabric.util.object.clone(target);

        switch (action.type) {
          case "fontFamily": {
            undoActionStack.push({
              id: iTextTarget.id as number,
              type: "fontFamily",
              object: clone,
            });

            iTextTarget.set({
              fontFamily: stackObject.fontFamily as string,
              dirty: true,
            });
            break;
          }
          case "fontSize": {
            undoActionStack.push({
              id: iTextTarget.id as number,
              type: "fontSize",
              object: clone,
            });

            iTextTarget.set({
              fontSize: stackObject.fontSize as number,
              dirty: true,
            });
            break;
          }
          case "fontWeight": {
            undoActionStack.push({
              id: iTextTarget.id as number,
              type: "fontWeight",
              object: clone,
            });

            iTextTarget.set({
              fontWeight: stackObject.fontWeight as string,
              dirty: true,
            });
            break;
          }
          case "fontStyle": {
            undoActionStack.push({
              id: iTextTarget.id as number,
              type: "fontStyle",
              object: clone,
            });

            iTextTarget.set({
              fontStyle: stackObject.fontStyle as "italic" | "normal",
              dirty: true,
            });
            break;
          }
          case "underline": {
            undoActionStack.push({
              id: iTextTarget.id as number,
              type: "underline",
              object: clone,
            });

            iTextTarget.set({
              underline: stackObject.underline as boolean,
              dirty: true,
            });
            break;
          }
          case "textAlign": {
            undoActionStack.push({
              id: iTextTarget.id as number,
              type: "textAlign",
              object: clone,
            });

            iTextTarget.set({
              textAlign: stackObject.textAlign as string,
              dirty: true,
            });
            break;
          }
        }

        canvas.value.setActiveObject(iTextTarget);
        canvas.value.renderAll();

        store.dispatch(ActionTypes.SET_ACCORDIAN_STATE, {
          ...accordianState.value,
          key: accordianState.value.key + 1,
        });
      }
      break;
    }
    case "stroke":
    case "strokeWidth":
    case "opacity":
    case "fill": {
      if (target && action.object) {
        const isGroup = (obj: fabric.Object): obj is fabric.Group => {
          const tmp = obj as fabric.Group;

          if (tmp._objects) {
            return true;
          } else {
            return false;
          }
        };

        const stackObject = action.object;

        switch (action.type) {
          case "opacity": {
            undoActionStack.push({
              id: target.id as number,
              type: "opacity",
              object: fabric.util.object.clone(target),
            });

            target.set({
              opacity: stackObject.opacity as number,
              dirty: true,
            });
            break;
          }
          case "stroke": {
            if (isGroup(target) && isGroup(stackObject)) {
              target.clone(function(clone: fabric.Group) {
                undoActionStack.push({
                  id: target.id as number,
                  type: "stroke",
                  object: clone,
                });
              });

              target._objects.forEach((obj) => {
                obj.set({
                  stroke: stackObject._objects[0].stroke as string,
                  dirty: true,
                });
              });
            } else {
              undoActionStack.push({
                id: target.id as number,
                type: "stroke",
                object: fabric.util.object.clone(target),
              });

              target.set({
                stroke: stackObject.stroke as string,
                dirty: true,
              });
            }
            break;
          }
          case "strokeWidth": {
            if (isGroup(target) && isGroup(stackObject)) {
              target.clone(function(clone: fabric.Group) {
                undoActionStack.push({
                  id: target.id as number,
                  type: "strokeWidth",
                  object: clone,
                });
              });

              target._objects.forEach((obj) => {
                obj.set({
                  strokeWidth: stackObject._objects[0].strokeWidth as number,
                  dirty: true,
                });
              });
            } else {
              undoActionStack.push({
                id: target.id as number,
                type: "strokeWidth",
                object: fabric.util.object.clone(target),
              });

              target.set({
                strokeWidth: stackObject.strokeWidth as number,
                dirty: true,
              });
            }
            break;
          }
          case "fill": {
            if (isGroup(target) && isGroup(stackObject)) {
              target.clone(function(clone: fabric.Group) {
                undoActionStack.push({
                  id: target.id as number,
                  type: "fill",
                  object: clone,
                });
              });

              target._objects.forEach((obj) => {
                obj.set({
                  fill: stackObject._objects[0].fill as string,
                  dirty: true,
                });
              });
            } else {
              undoActionStack.push({
                id: target.id as number,
                type: "fill",
                object: fabric.util.object.clone(target),
              });

              target.set({
                fill: stackObject.fill as string,
                dirty: true,
              });
            }
            break;
          }
        }

        canvas.value.setActiveObject(target);
        canvas.value.renderAll();

        store.dispatch(ActionTypes.SET_ACCORDIAN_STATE, {
          ...accordianState.value,
          key: accordianState.value.key + 1,
        });
        store.dispatch(ActionTypes.SET_CANVAS_ACTIVE_OBJECT, target);
      }
      break;
    }
    case "scaleX":
    case "scaleY": {
      if (target && action.object) {
        const stackObject = action.object;
        const clone = fabric.util.object.clone(target);

        switch (action.type) {
          case "scaleX": {
            undoActionStack.push({
              id: target.id as number,
              type: "scaleX",
              object: clone,
            });

            target.set({
              scaleX: stackObject.scaleX as number,
              dirty: true,
            });

            canvas.value.fire("object:actionStack", {
              target,
              transform: {
                action: "scaleX",
                original: clone,
              },
            });
            break;
          }
          case "scaleY": {
            undoActionStack.push({
              id: target.id as number,
              type: "scaleY",
              object: clone,
            });

            target.set({
              scaleY: stackObject.scaleY as number,
              dirty: true,
            });

            canvas.value.fire("object:actionStack", {
              target,
              transform: {
                action: "scaleY",
                original: clone,
              },
            });
            break;
          }
        }

        canvas.value.setActiveObject(target);
        canvas.value.renderAll();

        store.dispatch(ActionTypes.SET_ACCORDIAN_STATE, {
          ...accordianState.value,
          key: accordianState.value.key + 1,
        });
        store.dispatch(ActionTypes.SET_CANVAS_ACTIVE_OBJECT, target);
      }
      break;
    }
    case "strokeDashArray": {
      if (target && action.object) {
        const stackObject = action.object;

        undoActionStack.push({
          id: target.id as number,
          type: "strokeDashArray",
          object: fabric.util.object.clone(target),
        });

        target.set({
          strokeDashArray: stackObject.strokeDashArray as number[],
          dirty: true,
        });

        canvas.value.setActiveObject(target);
        canvas.value.renderAll();

        store.dispatch(ActionTypes.SET_ACCORDIAN_STATE, {
          ...accordianState.value,
          key: accordianState.value.key + 1,
        });
        store.dispatch(ActionTypes.SET_CANVAS_ACTIVE_OBJECT, target);
      }
      break;
    }
  }
};
